import { Button } from '@heidi/design-system/button';
import { HeidiLogoMark } from '@heidi/design-system/heidi-logo-mark';
import {
  DialogDrawer,
  DialogDrawerContent,
  DialogDrawerDescription,
  DialogDrawerTitle,
} from '@heidi/design-system/mobile-responsive-dialog-drawer';
import { FormattedMessage } from 'react-intl';

import { type WorkspaceSwitchPayload } from '../../data/atoms/workspaceAtoms';
import { useQueryTeamDetails } from '../../data/query/useQueryTeamDetails';
import { useQueryUser } from '../../data/query/useQueryUser';
import { joinWorkspaceDialogMessages } from './join-workspace-dialog.messages';
import { type JoinWorkspaceDialogPorts } from './ports';

interface Props {
  /** The invitation being acted on; `null` keeps the dialog closed. */
  invitation: { team_id: string; workspace_name?: string } | null;
  onClose: () => void;
  /** Ignore action. Defaults to closing; the launch prompt declines server-side. */
  onIgnore?: () => void;
  /** False pins the dialog open, for the forced post-signup prompt. */
  dismissible?: boolean;
  /** The app's accept delegate, failure modal and router, bound app-side. */
  ports: JoinWorkspaceDialogPorts;
}

/**
 * In-app confirmation dialog for joining a workspace from the workspace
 * switcher (MUL-2912). Same copy and accept logic as the legacy
 * `/join-team` page's `JoinTeamSameAccountModal`, but rendered over the app
 * instead of navigating to a blank full-screen page. The logged-out invite
 * flow (email link → `/join-team`) is untouched.
 */
export const JoinWorkspaceDialog = ({
  invitation,
  onClose,
  onIgnore,
  dismissible = true,
  ports,
}: Props) => {
  const { data: userEmail } = useQueryUser((user) => user.email);
  const teamId = invitation?.team_id;
  const { data: teamData } = useQueryTeamDetails(teamId);
  const {
    mutateAsync: acceptInvite,
    isError: isAcceptInviteError,
    isPending,
    reset,
  } = ports.useAcceptTeamInvitation();

  const closeAndReset = () => {
    reset();
    onClose();
  };

  if (isAcceptInviteError) {
    return <>{ports.renderJoinFailed({ teamId, onReturn: closeAndReset })}</>;
  }

  const onAcceptClick = async () => {
    if (!teamId || !userEmail) {
      return;
    }
    let switchedWorkspace: WorkspaceSwitchPayload | null;
    try {
      // Membership refetch can remove this dialog while the shared user refresh
      // is pending. Unlike per-call onSuccess, this continuation survives unmount.
      switchedWorkspace = await acceptInvite({
        teamId,
        invitedEmail: userEmail,
        joinMethod: 'LoggedWithSameAccount',
      });
    } catch {
      // The mutation owns the error state rendered by TeamMemberJoinFailModal.
      // Handle mutateAsync's rejection without navigating or accepting again.
      return;
    }
    onClose();
    if (switchedWorkspace !== null) {
      window.location.assign('/scribe');
    } else {
      void ports.onNavigate('/scribe');
    }
  };

  return (
    <DialogDrawer
      open={invitation !== null}
      onOpenChange={(open) => {
        if (!open && !isPending && dismissible) {
          closeAndReset();
        }
      }}
    >
      <DialogDrawerContent
        className="mx-auto flex flex-col items-center justify-center gap-4 p-[32px] backdrop-blur-[3px] sm:w-[500px]"
        hideCloseIcon
        data-testid="join-workspace-dialog"
      >
        <HeidiLogoMark className="size-4" />
        <div className="flex w-full flex-col items-center justify-center gap-2">
          <DialogDrawerTitle className="w-full p-0 text-center">
            <FormattedMessage
              {...joinWorkspaceDialogMessages.title}
              values={{
                teamName: teamData?.team.name ?? invitation?.workspace_name,
                br: () => <br />,
              }}
            />
          </DialogDrawerTitle>
          {userEmail && (
            <DialogDrawerDescription className="text-center">
              <FormattedMessage
                {...joinWorkspaceDialogMessages.inviteSentTo}
                values={{ email: userEmail }}
              />
            </DialogDrawerDescription>
          )}
        </div>
        <div className="flex w-full gap-2">
          <Button
            size="lg"
            disableAutomaticSvgSizing
            variant="ghost"
            className="flex-1"
            disabled={isPending}
            onClick={onIgnore ?? closeAndReset}
            data-testid="join-workspace-dialog-ignore"
          >
            <FormattedMessage {...joinWorkspaceDialogMessages.ignoreInvite} />
          </Button>
          <Button
            size="lg"
            disableAutomaticSvgSizing
            className="flex-1"
            disabled={isPending}
            onClick={() => void onAcceptClick()}
            data-testid="join-workspace-dialog-join"
          >
            <FormattedMessage {...joinWorkspaceDialogMessages.joinTeam} />
          </Button>
        </div>
      </DialogDrawerContent>
    </DialogDrawer>
  );
};
